import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendUp, Plus, Scales, Trash, Camera } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import AtalahChart from '@/components/AtalahChart';
import { Md } from '@/components/Md';
import InBodyCliente from '@/components/InBodyCliente';
import { isGravidaPatient } from '@/lib/utils';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EvolucaoPage() {
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const isGravida = isGravidaPatient(user);
  const [records, setRecords] = useState([]);
  const [goals, setGoals] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [expandedInbody, setExpandedInbody] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], peso: '', gordura_pct: '', massa_gorda: '' });
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);

  const fetchRecords = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/weight-records`); setRecords(data); } catch {} finally { setLoading(false); }
  }, []);
  const fetchGoals = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/my-goals`); setGoals(data); } catch {}
  }, []);
  const fetchPhotos = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/progress-photos/${user?.user_id}`); setPhotos(data || []); } catch {}
  }, [user]);
  useEffect(() => { fetchRecords(); fetchGoals(); fetchPhotos(); }, [fetchRecords, fetchGoals, fetchPhotos]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/api/progress-photos`, fd);
      toast.success('Foto guardada'); fetchPhotos();
    } catch { toast.error('Erro ao carregar foto'); }
    finally { setPhotoUploading(false); }
  };
  const deletePhoto = async (id) => {
    const ok = await confirmDelete({ description: 'Vai apagar esta foto de progresso.' });
    if (!ok) return;
    try { await axios.delete(`${API}/api/progress-photos/${id}`); toast.success('Foto eliminada'); fetchPhotos(); } catch {}
  };

  const handleAdd = async () => {
    if (!form.peso) return;
    try {
      await axios.post(`${API}/api/weight-records`, {
        ...form,
        peso: parseFloat(form.peso),
        gordura_pct: form.gordura_pct ? parseFloat(form.gordura_pct) : null,
        massa_gorda: form.massa_gorda ? parseFloat(form.massa_gorda) : null,
      });
      toast.success('Registo guardado');
      setShowAdd(false); setForm({ date: new Date().toISOString().split('T')[0], peso: '', gordura_pct: '', massa_gorda: '' }); fetchRecords();
    } catch { toast.error('Erro ao guardar'); }
  };

  const latest = records[0];
  const pesoObj = goals.peso_objetivo;
  const diff = latest && pesoObj ? (latest.peso - pesoObj).toFixed(1) : null;
  // Simple chart data: last 10 records reversed
  const chartData = records.slice(0, 10).reverse();
  const maxPeso = Math.max(...chartData.map(r => r.peso), pesoObj || 0) || 100;
  const minPeso = Math.min(...chartData.map(r => r.peso), pesoObj || 999) || 0;
  const range = maxPeso - minPeso || 1;

  return (
    <div className="fade-in" data-testid="evolucao-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text" data-testid="evolucao-title">A minha evolução</h2>
          <p className="text-sm text-brand-muted mt-1">Acompanha o teu progresso</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-weight-btn">
          <Plus size={16} /> Registar peso
        </button>
      </div>

      {/* Atalah curve for gravida */}
      {isGravida && (
        <div className="bg-white border border-brand-border rounded-3xl p-5 mb-5">
          <AtalahChart isClient />
        </div>
      )}

      {/* Current status */}
      {latest && (
        <div className="bg-white border border-brand-border rounded-3xl p-5 mb-5 shadow-[0_8px_32px_rgba(101,113,97,0.04)]" data-testid="weight-summary">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-heading font-semibold text-brand-text">{latest.peso}</p>
              <p className="text-[10px] text-brand-muted">Peso atual (kg)</p>
            </div>
            {pesoObj && (
              <div>
                <p className="text-2xl font-heading font-semibold text-brand-green">{pesoObj}</p>
                <p className="text-[10px] text-brand-muted">Objetivo (kg)</p>
              </div>
            )}
            {diff !== null && (
              <div>
                <p className={`text-2xl font-heading font-semibold ${parseFloat(diff) <= 0 ? 'text-brand-green' : 'text-brand-gold-hover'}`}>{diff > 0 ? `+${diff}` : diff}</p>
                <p className="text-[10px] text-brand-muted">Diferença (kg)</p>
              </div>
            )}
          </div>
          {goals.foco && <Md as="p" className="text-xs text-brand-muted mt-3 text-center">{'Foco: ' + goals.foco}</Md>}
        </div>
      )}

      {/* Simple weight chart */}
      {chartData.length >= 2 && (
        <div className="bg-white border border-brand-border rounded-3xl p-5 mb-5" data-testid="weight-chart">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-4">Evolução do peso</h3>
          <div className="relative h-40">
            {/* Goal line */}
            {pesoObj && (
              <div className="absolute left-0 right-0 border-t-2 border-dashed border-brand-green/30" style={{ bottom: `${((pesoObj - minPeso) / range) * 100}%` }}>
                <span className="absolute right-0 -top-4 text-[9px] text-brand-green">{pesoObj} kg</span>
              </div>
            )}
            {/* Data points and lines */}
            <svg className="w-full h-full" viewBox={`0 0 ${chartData.length * 60} 160`} preserveAspectRatio="none">
              {chartData.map((r, i) => {
                const x = i * 60 + 30;
                const y = 150 - ((r.peso - minPeso) / range) * 140;
                const next = chartData[i + 1];
                return (
                  <g key={i}>
                    {next && <line x1={x} y1={y} x2={(i + 1) * 60 + 30} y2={150 - ((next.peso - minPeso) / range) * 140} stroke="#657161" strokeWidth="2" />}
                    <circle cx={x} cy={y} r="4" fill="#657161" />
                  </g>
                );
              })}
            </svg>
            {/* Date labels */}
            <div className="flex justify-between mt-1">
              {chartData.map((r, i) => (
                <span key={i} className="text-[8px] text-brand-muted" style={{ width: '60px', textAlign: 'center' }}>{r.date?.slice(5)}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* History */}
      {loading ? <div className="text-center py-8 text-brand-muted">A carregar...</div> : records.length === 0 ? (
        <div className="text-center py-16">
          <Scales size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted">Sem registos de peso.</p>
          <p className="text-xs text-brand-placeholder mt-1">Regista o teu peso para acompanhar a evolução.</p>
        </div>
      ) : (
        <div>
          {/* Item 4 Fase 5 — médias semanais (cliente) */}
          {records.length > 1 && (() => {
            const isoWeek = (d) => { const dt = new Date(d + 'T00:00:00'); const day = dt.getUTCDay() || 7; dt.setUTCDate(dt.getUTCDate() + 4 - day); const ys = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1)); return `${dt.getUTCFullYear()}-W${String(Math.ceil((((dt - ys) / 86400000) + 1) / 7)).padStart(2, '0')}`; };
            const weekRange = (d) => { const dt = new Date(d + 'T00:00:00'); const day = (dt.getUTCDay() + 6) % 7; const mon = new Date(dt); mon.setUTCDate(dt.getUTCDate() - day); const sun = new Date(mon); sun.setUTCDate(mon.getUTCDate() + 6); const fmt = (x) => `${String(x.getUTCDate()).padStart(2, '0')} ${x.toLocaleString('pt-PT', { month: 'short', timeZone: 'UTC' }).replace('.', '')}`; return `${fmt(mon)}–${fmt(sun)}`; };
            const buckets = {};
            records.forEach(r => { if (r.is_weekly_average) return; const k = isoWeek(r.date); if (!buckets[k]) buckets[k] = { week: k, label: weekRange(r.date), values: [] }; buckets[k].values.push(parseFloat(r.peso) || 0); });
            const sorted = Object.values(buckets).filter(b => b.values.length >= 2).sort((a, b) => a.week.localeCompare(b.week));
            if (sorted.length === 0) return null;
            const withAvg = sorted.map(b => ({ ...b, avg: Math.round(b.values.reduce((a, c) => a + c, 0) / b.values.length * 10) / 10 }));
            return (
              <div className="bg-brand-bg/40 border border-brand-border rounded-2xl p-3 mb-4" data-testid="cliente-weekly-averages">
                <h4 className="text-[10px] text-brand-muted uppercase tracking-wider mb-2">Médias semanais</h4>
                <div className="space-y-1">
                  {withAvg.slice().reverse().map((b, idx) => {
                    const prev = withAvg.slice().reverse()[idx + 1];
                    const delta = prev ? Math.round((b.avg - prev.avg) * 10) / 10 : null;
                    return (
                      <div key={b.week} className="flex items-center justify-between text-xs flex-wrap" data-testid={`cliente-weekly-avg-${b.week}`}>
                        <span className="text-brand-text">Semana {b.label}: <strong>{b.avg.toFixed(1)} kg</strong> <span className="text-brand-muted">(média de {b.values.length})</span></span>
                        {delta != null && delta !== 0 && (
                          <span className={`text-[10px] font-medium ${delta < 0 ? 'text-brand-green' : 'text-red-500'}`}>{delta > 0 ? '+' : ''}{delta.toFixed(1)} kg face à semana anterior</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Histórico</h3>
          <div className="space-y-2">
            {records.map(r => (
              <div key={r.id} className={`border rounded-2xl overflow-hidden ${r.is_weekly_average ? 'bg-[#657161]/5 border-[#657161]/30' : 'bg-white border-brand-border'}`} data-testid={`weight-record-${r.id}`}>
                <div className="p-3 flex items-center justify-between cursor-pointer" onClick={() => r.inbody_data && setExpandedInbody(expandedInbody === r.id ? null : r.id)}>
                  <div>
                    <span className="text-xs text-brand-muted">{fmtDatePt(r.date)}</span>
                    {r.source === 'inbody' && <span className="ml-1.5 text-[9px] bg-brand-mint/15 text-brand-green px-1.5 py-0.5 rounded-full">InBody</span>}
                    {r.is_weekly_average && <span className="ml-1.5 text-[9px] bg-[#657161]/15 text-[#657161] px-1.5 py-0.5 rounded-full font-medium">Média semanal</span>}
                    {r.inbody_data && <span className="ml-1 text-[9px] text-brand-green">{expandedInbody === r.id ? 'Fechar' : 'Ver relatório'}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm font-heading font-semibold text-brand-text">{r.peso} kg</p>
                    </div>
                    {r.gordura_pct && <span className="text-xs text-brand-muted">{r.gordura_pct}%</span>}
                    {r.massa_gorda && <span className="text-xs text-brand-muted">{r.massa_gorda}kg MG</span>}
                    <button onClick={async (e) => { e.stopPropagation(); const ok = await confirmDelete({ description: `Vai apagar o registo de evolução de ${r.date || ''}${r.peso ? ` (${r.peso} kg)` : ''}.` }); if (!ok) return; await axios.delete(`${API}/api/weight-records/${r.id}`); toast.success('Registo eliminado'); fetchRecords(); }} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>
                  </div>
                </div>
                {expandedInbody === r.id && r.inbody_data && (
                  <div className="border-t border-brand-border/50">
                    <InBodyCliente
                      data={r.inbody_data}
                      firstName={user?.name?.split(' ')[0] || ''}
                      allRecords={records.filter(x => x.inbody_data).map(x => ({ date: x.date, data: x.inbody_data }))}
                      currentDate={r.date}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add record dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Registar peso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="weight-date" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Peso (kg)</Label><Input type="number" step="0.1" value={form.peso} onChange={e => setForm({ ...form, peso: e.target.value })} placeholder="Ex: 65.5" className="rounded-xl border-brand-border" required data-testid="weight-peso" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Gordura corporal % (opcional)</Label><Input type="number" step="0.1" value={form.gordura_pct} onChange={e => setForm({ ...form, gordura_pct: e.target.value })} placeholder="Ex: 25.0" className="rounded-xl border-brand-border" data-testid="weight-gordura" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Massa gorda (kg) (opcional)</Label><Input type="number" step="0.1" value={form.massa_gorda} onChange={e => setForm({ ...form, massa_gorda: e.target.value })} placeholder="Ex: 16.5" className="rounded-xl border-brand-border" data-testid="weight-massa-gorda" /></div>
            <button onClick={handleAdd} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-weight-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Progress photos */}
      <div className="mt-6" data-testid="progress-photos">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-medium text-sm text-brand-text">Fotos de progresso</h3>
          <label className={`bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1 cursor-pointer ${photoUploading ? 'opacity-50' : ''}`}>
            <Camera size={14} /> {photoUploading ? 'A carregar...' : 'Adicionar foto'}
            <input type="file" accept="image/*" onChange={e => uploadPhoto(e.target.files?.[0])} className="hidden" disabled={photoUploading} />
          </label>
        </div>
        {photos.length === 0 ? (
          <p className="text-center text-brand-muted text-xs py-4">Sem fotos de progresso. Adiciona a primeira.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {photos.map(p => (
              <div key={p.id} className="relative group rounded-xl overflow-hidden aspect-square" data-testid={`photo-${p.id}`}>
                <img src={`${API}/api/files/${p.photo_path}`} alt="" className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-1.5 py-1">
                  <p className="text-[9px] text-white">{fmtDatePt(p.date)}</p>
                </div>
                <button onClick={() => deletePhoto(p.id)} className="absolute top-1 right-1 hidden group-hover:flex w-6 h-6 bg-red-500 text-white rounded-full items-center justify-center"><Trash size={12} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
